import { Tooltip } from 'antd';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface OverflowTagsProps {
  tags: string[];
}

export const OverflowTags = ({ tags }: OverflowTagsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const moreMeasureRef = useRef<HTMLSpanElement>(null);

  const [visibleCount, setVisibleCount] = useState(tags.length);
  const [moreOnNewLine, setMoreOnNewLine] = useState(false);

  useLayoutEffect(() => {
    const calculate = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;

      const tagElements = Array.from(
        container.querySelectorAll('[data-measure-tag]'),
      ) as HTMLElement[];

      const moreWidth = (moreMeasureRef.current?.offsetWidth || 30) + 6;

      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < tagElements.length; i++) {
        const tagWidth = tagElements[i].offsetWidth + 6;

        usedWidth += tagWidth;
        count++;
      }

      let visible = count;

      if (tags.length > 1) {
        usedWidth = 0;
        visible = 0;

        for (let i = 0; i < tagElements.length; i++) {
          const tagWidth = tagElements[i].offsetWidth + 6;

          const remaining = tags.length - (i + 1);
          const reserve = remaining > 0 ? moreWidth : 0;

          if (i > 0 && usedWidth + tagWidth + reserve > containerWidth) {
            break;
          }

          usedWidth += tagWidth;
          visible++;
        }
      }

      visible = Math.max(1, visible);

      const hiddenCount = tags.length - visible;

      if (hiddenCount <= 0) {
        setVisibleCount(visible);
        setMoreOnNewLine(false);
        return;
      }

      const visibleWidth = tagElements
        .slice(0, visible)
        .reduce((sum, el) => sum + el.offsetWidth + 6, 0);

      const fitSameLine = visibleWidth + moreWidth <= containerWidth;

      if (fitSameLine) {
        setVisibleCount(visible);
        setMoreOnNewLine(false);
      } else if (visible > 1) {
        setVisibleCount(visible - 1);
        setMoreOnNewLine(false);
      } else {
        setVisibleCount(1);
        setMoreOnNewLine(true);
      }
    };

    calculate();

    const observer = new ResizeObserver(calculate);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [tags]);

  const visibleTags = tags.slice(0, visibleCount);
  const hiddenTags = tags.slice(visibleCount);

  const renderMoreTag = () => (
    <Tooltip
      title={
        <TooltipContent>
          {hiddenTags.map(tag => (
            <div key={tag}>{tag}</div>
          ))}
        </TooltipContent>
      }
    >
      <MoreTag>+{hiddenTags.length}</MoreTag>
    </Tooltip>
  );

  return (
    <TagContainer>
      <TagRow ref={containerRef}>
        <MeasureWrapper>
          {tags.map(tag => (
            <MeasureTag key={tag} data-measure-tag>
              {tag}
            </MeasureTag>
          ))}

          <MoreTag ref={moreMeasureRef}>+{hiddenTags.length || 99}</MoreTag>
        </MeasureWrapper>

        {visibleTags.map(tag => (
          <OfferTag key={tag} $danger={tag === 'non_refundable'}>
            {tag}
          </OfferTag>
        ))}

        {hiddenTags.length > 0 && !moreOnNewLine && renderMoreTag()}
      </TagRow>

      {hiddenTags.length > 0 && moreOnNewLine && renderMoreTag()}
    </TagContainer>
  );
};

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  min-width: 0;
`;

const MeasureWrapper = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
`;

const MeasureTag = styled.span`
  display: inline-block;
  white-space: nowrap;
  font-size: 11px;
  padding: 2px 8px;
`;

const OfferTag = styled.span<{ $danger?: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;

  background: ${p => (p.$danger ? '#fde8ea' : '#f2f2f2')};
  color: ${p => (p.$danger ? '#f06f24' : '#444')};
`;

const MoreTag = styled.span`
  width: fit-content;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f2f2f2;
  color: #444;
  white-space: nowrap;
  cursor: pointer;
`;

const TooltipContent = styled.div`
  max-width: 240px;
`;
